import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import queryString from "query-string";
import { endpoints } from "../../Feedback";
import { IAppState, ApiRequest } from "../../Common";
import { AdvancedSearchSettings } from "../Models/AdvancedSearch/AdvancedSearchSettings";
import { SearchContext } from "../Models/SearchContext";

interface IAdvancedSearchAction extends Action {
  type: AdvancedSearchActionType;
  payload: any;
}

// ARGUMENT TYPES

export interface IUpdateAdvancedSearchSettingsArguments {
  settings: AdvancedSearchSettings;
}

export interface IGetFeedbackCreationEventsArguments {
  start: Date;
  end: Date;
  context: SearchContext;
  utcOffset: number;
}

// PAYLOAD TYPES

export interface IToggleAdvancedSearchDropdownPayload {}

export interface IToggleTimelineDropdownPayload {}

export interface IUpdateAdvancedSearchSettingsPayload {
  settings: AdvancedSearchSettings;
}

export interface ISetFeedbackCreationEventsLoadedPayload {
  events: any[];
}

export interface ISetFeedbackCreationEventsLoadingPayload {}

export interface ISetFeedbackCreationEventsErroredPayload {
  errorCode: number;
  errorDescription: string;
}

// ACTION TYPES

export interface IToggleAdvancedSearchDropdownAction extends IAdvancedSearchAction {
  payload: IToggleAdvancedSearchDropdownPayload;
}

export interface IToggleTimelineDropdownAction extends IAdvancedSearchAction {
  payload: IToggleTimelineDropdownPayload;
}

export interface IUpdateAdvancedSearchSettingsAction extends IAdvancedSearchAction {
  payload: IUpdateAdvancedSearchSettingsPayload;
}

export interface ISetFeedbackCreationEventsLoadedAction extends IAdvancedSearchAction {
  payload: ISetFeedbackCreationEventsLoadedPayload;
}

export interface ISetFeedbackCreationEventsLoadingAction extends IAdvancedSearchAction {
  payload: ISetFeedbackCreationEventsLoadingPayload;
}

export interface ISetFeedbackCreationEventsErroredAction extends IAdvancedSearchAction {
  payload: ISetFeedbackCreationEventsErroredPayload;
}

// union of all action types. If you define a new action type, add it here pls
export type AdvancedSearchAction =
  | IToggleAdvancedSearchDropdownAction
  | IToggleTimelineDropdownAction
  | IUpdateAdvancedSearchSettingsAction
  | ISetFeedbackCreationEventsLoadedAction
  | ISetFeedbackCreationEventsLoadingAction
  | ISetFeedbackCreationEventsErroredAction;

export enum AdvancedSearchActionType {
  TOGGLE_ADVANCED_SEARCH_DROPDOWN = "TOGGLE_ADVANCED_SEARCH_DROPDOWN",
  TOGGLE_TIMELINE_DROPDOWN = "TOGGLE_TIMELINE_DROPDOWN",
  UPDATE_ADVANCED_SEARCH_SETTINGS = "UPDATE_ADVANCED_SEARCH_SETTINGS",
  SET_FEEDBACK_CREATION_EVENTS_LOADED = "SET_FEEDBACK_CREATION_EVENTS_LOADED",
  SET_FEEDBACK_CREATION_EVENTS_LOADING = "SET_FEEDBACK_CREATION_EVENTS_LOADING",
  SET_FEEDBACK_CREATION_EVENTS_ERRORED = "SET_FEEDBACK_CREATION_EVENTS_ERRORED"
}

export const toggleAdvancedSearchDropdown = (): IToggleAdvancedSearchDropdownAction => ({
  type: AdvancedSearchActionType.TOGGLE_ADVANCED_SEARCH_DROPDOWN,
  payload: {}
});

export const toggleTimelineDropdown = (): IToggleTimelineDropdownAction => ({
  type: AdvancedSearchActionType.TOGGLE_TIMELINE_DROPDOWN,
  payload: {}
});

export const updateAdvancedSearchSettings = (
  args: IUpdateAdvancedSearchSettingsArguments
): IUpdateAdvancedSearchSettingsAction => ({
  type: AdvancedSearchActionType.UPDATE_ADVANCED_SEARCH_SETTINGS,
  payload: { ...args }
});

export const setFeedbackCreationEventsLoading = (): ISetFeedbackCreationEventsLoadingAction => ({
  type: AdvancedSearchActionType.SET_FEEDBACK_CREATION_EVENTS_LOADING,
  payload: {}
});

const setFeedbackCreationEventsErrored = (
  errorCode: number,
  errorDescription: string
): ISetFeedbackCreationEventsErroredAction => ({
  type: AdvancedSearchActionType.SET_FEEDBACK_CREATION_EVENTS_ERRORED,
  payload: { errorCode, errorDescription }
});

function assembleGetFeedbackCreationEventsUrl(args: IGetFeedbackCreationEventsArguments): string {
  let url: string = endpoints.feedback.quickSearch();

  const queryArgs = {
    startRange: args.start.toISOString(),
    endRange: args.end.toISOString(),
    context: args.context.type.toString(),
    contextItemId: args.context.itemId
  };

  const stringified = queryString.stringify(queryArgs);

  url += `?${stringified}`;

  return url;
}

export const getFeedbackCreationEvents = (
  args: IGetFeedbackCreationEventsArguments
): ThunkAction<
  void,
  IAppState,
  {},
  ISetFeedbackCreationEventsLoadedAction | ISetFeedbackCreationEventsErroredAction
> => dispatch => {
  ApiRequest.fromPath(assembleGetFeedbackCreationEventsUrl(args))
    .withAuthHeader()
    .get()
    .then(response => response.json())
    .then(json => {
      if (json.status === undefined) {
        // this was successful

        // parse each part of the json's date
        const parsedEvents: any[] = [];
        for (let i = 0; i < json.length; i++) {
          const date = new Date(Date.parse(json[i].date));

          // convert to local time for user
          date.setTime(date.getTime() + args.utcOffset * 60 * 60 * 1000);

          parsedEvents.push({
            Id: json[i].id,
            Date: date
          });
        }

        dispatch({
          type: AdvancedSearchActionType.SET_FEEDBACK_CREATION_EVENTS_LOADED,
          payload: { events: parsedEvents }
        });
      } else {
        // this failed
        dispatch(setFeedbackCreationEventsErrored(json.status, json.title));
      }
    });
};
